import {useCallback, useContext, useMemo} from 'react';

import {
  ReportDiscussionContext,
  ReportDiscussionUpdaterContext,
} from '../../components/ReportDiscussion/ReportDiscussionContext';
import {type CommentableRef} from '../../components/ReportDiscussion/types';
import {useDiscussionThreads} from '../views/discussionThread/hooks';
import {type Ref as DiscussionThreadRef} from '../views/discussionThread/types';
import * as ViewHooks from '../views/hooks';

// For managing discussion/comments on a single panel
// Currently it hackily scans all the discussion threads looking for references to the panel
// Ok for now, but we might want to optimize this later e.g., by associating in redux.
export function usePanelComments(props: {panelRef: CommentableRef}) {
  const {discussionThreadRefs, panelCommentsEnabled, highlightIds} = useContext(
    ReportDiscussionContext
  );

  const {appendPanelMentionToComment, setActiveThread} = useContext(
    ReportDiscussionUpdaterContext
  );

  const panel = ViewHooks.usePart(props.panelRef);
  const allDiscussionThreads = useDiscussionThreads(discussionThreadRefs);

  const {panelDiscussionThreadRefs, panelCommentCount} = useMemo(() => {
    let commentCount = 0;
    const pnlDiscussionThreadRefs: DiscussionThreadRef[] = [];

    allDiscussionThreads.forEach((dt, i) => {
      if (
        dt.comments
          .map(comment => comment.body)
          .some(comment => comment.includes(`(${panel.__id__})`))
      ) {
        commentCount = commentCount + 1;
        pnlDiscussionThreadRefs.push(discussionThreadRefs[i]);
      }
    });

    return {
      panelDiscussionThreadRefs: pnlDiscussionThreadRefs,
      panelCommentCount: commentCount,
    };
  }, [allDiscussionThreads, panel.__id__, discussionThreadRefs]);

  const openPanelComments = useCallback(() => {
    setActiveThread(
      panelDiscussionThreadRefs[0],
      true,
      panelDiscussionThreadRefs
    );
  }, [panelDiscussionThreadRefs, setActiveThread]);

  const addComment = useCallback(() => {
    appendPanelMentionToComment(panel.__id__);
  }, [appendPanelMentionToComment, panel.__id__]);

  const isHighlighted = highlightIds.includes(panel.__id__);

  const result = useMemo(
    () =>
      // Panel comments are disabled in old reports until the author saves them again, because the panels don't have persistent IDs
      // See ActionsInternal.loadFinished in reducer.ts for more info
      panelCommentsEnabled
        ? {
            panelCommentCount,
            openPanelComments,
            addComment,
            isHighlighted,
          }
        : {},
    [
      addComment,
      isHighlighted,
      openPanelComments,
      panelCommentCount,
      panelCommentsEnabled,
    ]
  );

  return result;
}
